import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Column, DataTableCustomization } from "@/components/organisms/DataTable";
import { useProfileStore } from "@/store/profileStore";
import {
  clampTableColumnWidth,
  defaultTableColumnPreferences,
  parseStoredTableColumnPreferences,
  serializeTableColumnPreferences,
  tableColumnStorageKey,
  type TableColumnPreferences,
} from "@/lib/tableColumnPreferences";

function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, preferences: TableColumnPreferences) {
  try {
    window.localStorage.setItem(key, serializeTableColumnPreferences(preferences));
  } catch {
    // Storage can be unavailable in private browsing or under a strict policy.
  }
}

function safeRemove(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Reset still applies in memory when storage is unavailable.
  }
}

function isDefaultPreferences(
  preferences: TableColumnPreferences,
  columnIds: readonly string[],
  defaultHiddenColumnIds: readonly string[],
): boolean {
  const expectedHidden = defaultHiddenColumnIds.filter((id) => columnIds.includes(id)).sort();
  const actualHidden = [...preferences.hidden].sort();
  return actualHidden.length === expectedHidden.length &&
    actualHidden.every((id, index) => id === expectedHidden[index]) &&
    Object.keys(preferences.widths).length === 0 &&
    preferences.order.length === columnIds.length &&
    preferences.order.every((id, index) => id === columnIds[index]);
}

export function useTableColumnPreferences<T>(
  columns: Column<T>[],
  customization?: DataTableCustomization,
) {
  const profile = useProfileStore((state) => state.profileData);
  const userId = profile?.userId?.trim() || profile?.email?.trim().toLowerCase() || null;
  const columnSignature = columns.map((column) => column.id).join("|");
  const columnIds = useMemo(
    () => columnSignature ? columnSignature.split("|") : [],
    [columnSignature],
  );
  const lockedSignature = [
    ...(customization?.lockedColumnIds ?? []),
    ...columns
      .filter((column) => column.headerAccessory && !customization?.optionalColumnIds?.includes(column.id))
      .map((column) => column.id),
  ].filter((id, index, ids) => ids.indexOf(id) === index).join("|");
  const pinnedStartSignature = (customization?.pinnedStartColumnIds ?? []).join("|");
  const pinnedEndSignature = (customization?.pinnedEndColumnIds ?? []).join("|");
  const defaultHiddenSignature = (customization?.defaultHiddenColumnIds ?? []).join("|");
  const defaultHiddenColumnIds = useMemo(
    () => defaultHiddenSignature ? defaultHiddenSignature.split("|") : [],
    [defaultHiddenSignature],
  );

  // Header-filter columns remain visible so an active filter never becomes an
  // invisible constraint and continues to participate in the filter tally.
  const lockedColumnIds = useMemo(
    () => new Set(lockedSignature ? lockedSignature.split("|") : []),
    [lockedSignature],
  );
  const pinnedStart = useMemo(
    () => new Set(pinnedStartSignature ? pinnedStartSignature.split("|") : []),
    [pinnedStartSignature],
  );
  const pinnedEnd = useMemo(
    () => new Set(pinnedEndSignature ? pinnedEndSignature.split("|") : []),
    [pinnedEndSignature],
  );
  const fixedColumnIds = useMemo(
    () => new Set([...pinnedStart, ...pinnedEnd]),
    [pinnedStart, pinnedEnd],
  );
  const storageKey = useMemo(
    () => customization && userId ? tableColumnStorageKey(customization.tableId, userId) : null,
    [customization, userId],
  );

  const [preferences, setPreferences] = useState<TableColumnPreferences>(() =>
    defaultTableColumnPreferences(columnIds, defaultHiddenColumnIds),
  );
  const preferencesRef = useRef(preferences);
  const [draftWidths, setDraftWidths] = useState<Record<string, number>>({});
  const draftWidthsRef = useRef(draftWidths);
  const [hydratedStorageKey, setHydratedStorageKey] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!storageKey) {
      const defaults = defaultTableColumnPreferences(columnIds, defaultHiddenColumnIds);
      preferencesRef.current = defaults;
      setHydratedStorageKey(null);
      setPreferences(defaults);
      draftWidthsRef.current = {};
      setDraftWidths({});
      return;
    }
    const stored = parseStoredTableColumnPreferences(
      safeRead(storageKey),
      columnIds,
      lockedColumnIds,
      defaultHiddenColumnIds,
    );
    preferencesRef.current = stored;
    setPreferences(stored);
    draftWidthsRef.current = {};
    setDraftWidths({});
    setHydratedStorageKey(storageKey);
  }, [storageKey, columnIds, lockedColumnIds, defaultHiddenColumnIds]);

  const commit = useCallback((update: (current: TableColumnPreferences) => TableColumnPreferences) => {
    const next = update(preferencesRef.current);
    preferencesRef.current = next;
    setPreferences(next);
  }, []);

  useEffect(() => {
    if (!storageKey || hydratedStorageKey !== storageKey) return;

    const timeout = window.setTimeout(() => {
      if (isDefaultPreferences(preferences, columnIds, defaultHiddenColumnIds)) {
        safeRemove(storageKey);
      } else {
        safeWrite(storageKey, preferences);
      }
    }, 150);

    return () => window.clearTimeout(timeout);
  }, [preferences, storageKey, hydratedStorageKey, columnIds, defaultHiddenColumnIds]);

  const orderedColumns = useMemo(() => {
    const byId = new Map(columns.map((column) => [column.id, column]));
    const preferred = preferences.order.map((id) => byId.get(id)).filter(Boolean) as Column<T>[];
    const missing = columns.filter((column) => !preferences.order.includes(column.id));
    const complete = [...preferred, ...missing];
    return [
      ...complete.filter((column) => pinnedStart.has(column.id)),
      ...complete.filter((column) => !pinnedStart.has(column.id) && !pinnedEnd.has(column.id)),
      ...complete.filter((column) => pinnedEnd.has(column.id)),
    ];
  }, [columns, preferences.order, pinnedStart, pinnedEnd]);

  const visibleColumns = useMemo(
    () => orderedColumns.filter((column) => lockedColumnIds.has(column.id) || !preferences.hidden.includes(column.id)),
    [orderedColumns, lockedColumnIds, preferences.hidden],
  );

  const visibleDataColumnCount = visibleColumns.filter((column) => !pinnedEnd.has(column.id)).length;

  const toggleColumn = useCallback((id: string) => {
    if (lockedColumnIds.has(id)) return;
    commit((current) => {
      const isHidden = current.hidden.includes(id);
      if (!isHidden) {
        const currentVisibleDataColumnCount = columnIds.filter((columnId) =>
          !pinnedEnd.has(columnId) &&
          (lockedColumnIds.has(columnId) || !current.hidden.includes(columnId)),
        ).length;
        if (currentVisibleDataColumnCount <= 1) return current;
      }
      return {
        ...current,
        hidden: isHidden ? current.hidden.filter((columnId) => columnId !== id) : [...current.hidden, id],
      };
    });
  }, [commit, lockedColumnIds, columnIds, pinnedEnd]);

  const moveColumn = useCallback((id: string, direction: -1 | 1) => {
    if (fixedColumnIds.has(id)) return;
    commit((current) => {
      const movable = current.order.filter((columnId) => !fixedColumnIds.has(columnId));
      const index = movable.indexOf(id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= movable.length) return current;
      [movable[index], movable[target]] = [movable[target], movable[index]];

      let movableIndex = 0;
      return {
        ...current,
        order: current.order.map((columnId) =>
          fixedColumnIds.has(columnId) ? columnId : movable[movableIndex++]),
      };
    });
  }, [commit, fixedColumnIds]);

  const setColumnWidth = useCallback((id: string, width: number) => {
    commit((current) => ({
      ...current,
      widths: {
        ...current.widths,
        [id]: clampTableColumnWidth(width, id === "actions" ? 64 : undefined),
      },
    }));
  }, [commit]);

  const setDraftColumnWidth = useCallback((id: string, width: number) => {
    const next = {
      ...draftWidthsRef.current,
      [id]: clampTableColumnWidth(width, id === "actions" ? 64 : undefined),
    };
    draftWidthsRef.current = next;
    setDraftWidths(next);
  }, []);

  const persistColumnWidths = useCallback(() => {
    const draft = draftWidthsRef.current;
    if (Object.keys(draft).length === 0) return;

    const next: TableColumnPreferences = {
      ...preferencesRef.current,
      widths: { ...preferencesRef.current.widths, ...draft },
    };
    preferencesRef.current = next;
    draftWidthsRef.current = {};
    setPreferences(next);
    setDraftWidths({});

    if (storageKey && hydratedStorageKey === storageKey) {
      if (isDefaultPreferences(next, columnIds, defaultHiddenColumnIds)) {
        safeRemove(storageKey);
      } else {
        safeWrite(storageKey, next);
      }
    }
  }, [storageKey, hydratedStorageKey, columnIds, defaultHiddenColumnIds]);

  const reset = useCallback(() => {
    if (storageKey) safeRemove(storageKey);
    const defaults = defaultTableColumnPreferences(columnIds, defaultHiddenColumnIds);
    preferencesRef.current = defaults;
    draftWidthsRef.current = {};
    setPreferences(defaults);
    setDraftWidths({});
  }, [storageKey, columnIds, defaultHiddenColumnIds]);

  return {
    orderedColumns,
    visibleColumns: customization ? visibleColumns : columns,
    widths: preferences.widths,
    draftWidths,
    lockedColumnIds,
    fixedColumnIds,
    toggleColumn,
    moveColumn,
    setColumnWidth,
    setDraftColumnWidth,
    persistColumnWidths,
    reset,
    canHideColumn: (id: string) =>
      !lockedColumnIds.has(id) &&
      (preferences.hidden.includes(id) || visibleDataColumnCount > 1),
  };
}
